"use client";

import React, { useState, useMemo, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { stripHTML } from "@/utils/stripHTML";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { parseHtmlJson } from "@/utils/parseHtmlTextToJson";
import {
  useFetchProductCollection,
  useFetchProductCount,
} from "@/apis/queryFunctions/productFunctions";
import { useFetchDmnvts } from "@/apis/queryFunctions/categoryFunctions";
import Container from "@/components/Layouts/Container/Container";
import ProductCard from "@/components/Product/ProductCard";
import TopFilterBar from "@/components/Product/TopFilterBar";
import Pagination from "@/components/Product/Pagination";
import ActionButton from "@/components/Common/ActionButton";
import Title from "@/components/Common/Typography/Title";
import Description from "@/components/Common/Typography/Description";
import { useHeader } from "@/context/HeaderLayoutContext";
import { ProductProvider } from "@/context/ProductContext";
import productColors from "@/mockDatas/productColors.json";
import { getTabColor } from "@/utils/getProductColor";
import styles from "./introduction.module.css";

const PAGE_SIZE = Number(process.env.COLLECTION_LOADING_NUMBER || 16);

function getSortObj(sortKey: string) {
  switch (sortKey) {
    case "price-asc":
      return { gia_ban_le: 1 };
    case "price-desc":
      return { gia_ban_le: -1 };
    case "name-asc":
      return { ten_vt: 1 };
    default:
      return { date_updated: -1 };
  }
}

interface ProductIntroductionClientProps {
  data: any;
  locale: string;
  slug?: string;
}

export default function ProductIntroductionClient({
  data,
  locale = "vi",
  slug = "",
}: ProductIntroductionClientProps) {
  const { setHeaderStyle } = useHeader();

  useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  //#region 1. Banner Data & Logic (Dùng chung chuẩn Trang Giới Thiệu)
  const bannerRecord = useMemo(() => {
    const list = Array.isArray(data?.add_on_data) ? data.add_on_data : [];
    return (
      list.find(
        (item: any) =>
          item?.title?.toLowerCase() === "banner" ||
          item?.title?.toLowerCase()?.includes("banner") ||
          item?.stt === 1
      ) ||
      list[0] ||
      null
    );
  }, [data]);

  const isBannerVisible = bannerRecord
    ? bannerRecord.shared !== false &&
      bannerRecord.shared !== "false" &&
      bannerRecord.shared !== 0 &&
      bannerRecord.shared !== "0"
    : true;

  const bannerPictures = useMemo(() => {
    if (!bannerRecord) return [];
    return [
      bannerRecord.picture,
      bannerRecord.picture_2,
      bannerRecord.picture_3,
      bannerRecord.picture_4,
      bannerRecord.picture_5,
      bannerRecord.picture_6,
    ].filter(Boolean);
  }, [bannerRecord]);

  const bannerTabs = useMemo(() => {
    if (!bannerRecord) return [];
    try {
      const parsed = parseHtmlJson(bannerRecord.content);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((item: any, idx: number) => ({
          tab: item.tab || item.title || `Tab ${idx + 1}`,
          title: item.title || "",
          description: item.description || item.content || "",
          picture:
            bannerPictures[idx] ||
            bannerPictures[0] ||
            bannerRecord.picture ||
            "",
        }));
      }
    } catch (error) {
      console.error("Error parsing banner content JSON:", error);
    }

    // Fallback nếu content là text / HTML thông thường
    return [
      {
        tab: bannerRecord.summary || bannerRecord.title || "Giới thiệu",
        title: bannerRecord.summary || bannerRecord.title || data?.title || "",
        description: bannerRecord.content || "",
        picture: bannerPictures[0] || bannerRecord.picture || "",
      },
    ];
  }, [bannerRecord, bannerPictures, data?.title]);

  const [activeIndex, setActiveIndex] = useState(0);
  const activeTab = bannerTabs[activeIndex] || bannerTabs[0];
  const activeBannerImg =
    activeTab?.picture || bannerPictures[0] || bannerRecord?.picture || "";
  //#endregion

  //#region 2. Catalog & Sector Config
  const catalogRecord = useMemo(() => {
    const list = Array.isArray(data?.add_on_data) ? data.add_on_data : [];
    return (
      list.find(
        (item: any) =>
          item?.title?.toLowerCase() === "tat-ca-san-pham" ||
          item?.title?.toLowerCase()?.includes("tat-ca") ||
          item?.stt === 2
      ) ||
      list[1] ||
      null
    );
  }, [data]);

  const catalogTitle =
    catalogRecord?.summary || data?.title || "Tất cả sản phẩm";
  const catalogSubtitle =
    catalogRecord?.content_2 ||
    "Khám phá trọn vẹn danh mục sản phẩm chất lượng của Việt Thiên";

  // Parse config nếu có trong content của catalogRecord (fallback productColors.json)
  const productConfig = useMemo(() => {
    const fromContent = parseHtmlJson<any>(catalogRecord?.content);
    return fromContent || productColors;
  }, [catalogRecord?.content]);

  // Nhận diện mã ngành hàng (ưu tiên 1 là introData.summary từ CMS như "CAFE", "TRA", "NS", "TPCB")
  const currentSectorCode = useMemo(() => {
    const rawSummary = stripHTML(data?.summary || "")
      .trim()
      .toUpperCase();
    if (
      rawSummary === "CAFE" ||
      rawSummary === "CÀ PHÊ" ||
      rawSummary === "CA PHE"
    )
      return "CAFE";
    if (rawSummary === "TRA" || rawSummary === "TRÀ") return "TRA";
    if (
      rawSummary === "NS" ||
      rawSummary === "NÔNG SẢN" ||
      rawSummary === "NONG SAN"
    )
      return "NS";
    if (
      rawSummary === "TPCB" ||
      rawSummary === "THỰC PHẨM CHẾ BIẾN" ||
      rawSummary === "THUC PHAM CHE BIEN"
    )
      return "TPCB";
    if (
      rawSummary === "NGK" ||
      rawSummary === "NƯỚC GIẢI KHÁT" ||
      rawSummary === "NUOC GIAI KHAT"
    )
      return "NGK";
    if (
      rawSummary === "DA" ||
      rawSummary === "DẦU ĂN" ||
      rawSummary === "DAU AN"
    )
      return "DA";
    if (rawSummary) return rawSummary;

    // Fallback theo slug nếu CMS chưa gắn summary
    const s = String(slug || "").toLowerCase();
    if (s.includes("ca-phe") || s.includes("coffee") || s.includes("cafe"))
      return "CAFE";
    if (s.includes("tra") || s.includes("tea")) return "TRA";
    if (s.includes("nong-san") || s.includes("ns")) return "NS";
    if (s.includes("thuc-pham") || s.includes("che-bien") || s.includes("tpcb"))
      return "TPCB";
    if (s.includes("nuoc-giai-khat") || s.includes("ngk")) return "NGK";
    if (s.includes("dau-an") || s.includes("da")) return "DA";
    return "";
  }, [data?.summary, slug]);

  const allowedNganhHang = useMemo<string[]>(() => {
    // Với trang giới thiệu ngành hàng (trang con), ngành hàng của trang này (từ data.summary) luôn là ưu tiên số 1
    if (currentSectorCode) {
      return [currentSectorCode];
    }
    // Nếu có config riêng 1 ngành hàng trong content của catalogRecord
    if (
      Array.isArray(productConfig?.allowed_ma_nganh_hang) &&
      productConfig.allowed_ma_nganh_hang.length === 1
    ) {
      return productConfig.allowed_ma_nganh_hang;
    }
    return ["TRA", "CAFE", "NS", "TPCB"];
  }, [currentSectorCode, productConfig]);

  const groupDefinitions = useMemo(() => {
    return Array.isArray(productConfig?.group_definitions)
      ? productConfig.group_definitions
      : productColors?.group_definitions || [];
  }, [productConfig]);
  //#endregion

  //#region 3. Product Listing & Filter State
  const [page, setPage] = useState(1);
  const [sortQuery, setSortQuery] = useState("newest");
  const [selectedDmnvts, setSelectedDmnvts] = useState<string[]>([]);
  const [gridCols, setGridCols] = useState<3 | 4>(4);

  // Fetch toàn bộ nhóm ngành hàng (dmnvt) để lọc
  const { data: allDmnvts } = useFetchDmnvts({
    condition: { shared: true, status: true },
  });

  // Lấy các category IDs thuộc ngành hàng này
  const allDmnvtIds = useMemo(() => {
    return (allDmnvts || [])
      .filter((item: any) => {
        if (!item?.ma_nganh_hang) return false;
        const itemSector = String(item.ma_nganh_hang).toUpperCase().trim();
        return allowedNganhHang.some(
          (code: string) => String(code).toUpperCase().trim() === itemSector
        );
      })
      .map((item: any) => item._id);
  }, [allDmnvts, allowedNganhHang]);

  // Category IDs thực tế dùng khi query sản phẩm
  const activeDmnvtIds =
    selectedDmnvts.length > 0 ? selectedDmnvts : allDmnvtIds;

  const fetchOptions = useMemo(
    () => ({
      page,
      sort: getSortObj(sortQuery),
      condition: {},
    }),
    [page, sortQuery]
  );

  const {
    data: products,
    isLoading,
    isFetched,
  } = useFetchProductCollection(fetchOptions, activeDmnvtIds);

  const { data: productCount } = useFetchProductCount({
    options: { condition: { ma_nvt: activeDmnvtIds } },
  });

  const totalPages = Math.max(1, Math.ceil((productCount || 0) / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [selectedDmnvts, sortQuery]);

  const skeletons = useMemo(() => new Array(PAGE_SIZE).fill(null), []);

  const handleReset = () => {
    setSelectedDmnvts([]);
    setSortQuery("newest");
    setPage(1);
  };
  //#endregion

  //#region 4. Product Showcase Data & Logic (add_on_data_2 so le)
  const introSectionConfig = useMemo(() => {
    const list = Array.isArray(data?.add_on_data) ? data.add_on_data : [];
    return (
      list.find(
        (item: any) => item?.title?.toLowerCase() === "gioi-thieu-san-pham"
      ) || null
    );
  }, [data?.add_on_data]);

  // Kiểm tra bật / tắt phần giới thiệu tổng thể theo shared
  const isIntroSectionVisible = introSectionConfig
    ? introSectionConfig.shared !== false &&
      introSectionConfig.shared !== "false" &&
      introSectionConfig.shared !== 0 &&
      introSectionConfig.shared !== "0"
    : true;

  // Danh sách các mục giới thiệu sản phẩm (add_on_data_2)
  const introItems = useMemo(() => {
    const list2 = Array.isArray(data?.add_on_data_2) ? data.add_on_data_2 : [];
    let rawList = list2;

    // Fallback nếu CMS lưu trong add_on_data
    if (rawList.length === 0 && Array.isArray(data?.add_on_data)) {
      rawList = data.add_on_data.filter(
        (item: any) =>
          item?.title?.toLowerCase() === "gioi-thieu-san-pham" &&
          (item?.summary || item?.content || item?.picture)
      );
    }

    return rawList
      .filter((item: any) => {
        // Kiểm tra shared để bật / tắt từng item
        const isShared =
          item?.shared !== false &&
          item?.shared !== "false" &&
          item?.shared !== 0 &&
          item?.shared !== "0";
        const hasContent = item?.summary || item?.content || item?.picture;
        return isShared && hasContent;
      })
      .sort((a: any, b: any) => {
        // Sắp xếp theo stt trước, sau đó theo line (timestamp) tăng dần
        if (a?.stt !== undefined && b?.stt !== undefined && a.stt !== b.stt) {
          return (Number(a.stt) || 0) - (Number(b.stt) || 0);
        }
        return (Number(a?.line) || 0) - (Number(b?.line) || 0);
      });
  }, [data?.add_on_data_2, data?.add_on_data]);

  // Lấy màu thương hiệu của ngành hàng để đồng bộ cho line deco
  const sectorThemeColor = useMemo(() => {
    return getTabColor(
      { id: "all", label: "Tất cả", ma_nganh_hang: currentSectorCode },
      productConfig,
      currentSectorCode
    );
  }, [productConfig, currentSectorCode]);
  //#endregion

  return (
    <ProductProvider initialConfig={productConfig}>
      <div className={styles.wrapper}>
        {/* ═══ 1. HERO BANNER (CHUẨN TRANG GIỚI THIỆU) ═══ */}
        {isBannerVisible && (
          <section className={styles.bannerSection}>
            {activeBannerImg && (
              <Image
                src={appendImageUrlFromAPI({
                  src: activeBannerImg,
                  size: "2k",
                })}
                alt={activeTab?.title || "Banner"}
                fill
                priority
                sizes="100vw"
                className={styles.bannerBgImage}
              />
            )}

            <div className={styles.bannerContent}>
              <div className={styles.textBlock}>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`intro-banner-${activeIndex}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  >
                    {activeTab?.title && (
                      <Title
                        size="medium"
                        as="h1"
                        color="#ffffff"
                        align="left"
                        style={{
                          marginBottom: "14px",
                          textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
                        }}
                      >
                        {activeTab.title}
                      </Title>
                    )}

                    {activeTab?.description && (
                      <Description
                        as="div"
                        size="medium"
                        color="rgba(255, 255, 255, 0.95)"
                        align="left"
                        className={styles.bannerDesc}
                        dangerouslySetInnerHTML={{
                          __html: activeTab.description,
                        }}
                      />
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Tabs chuyển đổi nếu có nhiều banner item */}
                {bannerTabs.length > 1 && (
                  <div className={styles.navGroup}>
                    {bannerTabs.map((item: any, index: number) => {
                      const isActive = activeIndex === index;
                      return (
                        <ActionButton
                          key={index}
                          label={item.tab}
                          onClick={() => setActiveIndex(index)}
                          color={
                            isActive ? "#008748" : "rgba(255, 255, 255, 0.2)"
                          }
                          hoverColor="#008748"
                          showIcon={false}
                          style={{
                            padding: "8px 20px",
                            fontSize: "13px",
                            fontWeight: 600,
                            border: isActive
                              ? "1px solid #008748"
                              : "1px solid rgba(255, 255, 255, 0.4)",
                            backdropFilter: "blur(8px)",
                            WebkitBackdropFilter: "blur(8px)",
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ═══ 2. LISTING HEADER ═══ */}
        <section className={styles.listingHeader}>
          {/* <h2 className={styles.listingTitle}>{catalogTitle}</h2> */}
          <Description
            size="medium"
            align="center"
            className={styles.listingSubtitle}
          >
            {catalogSubtitle}
          </Description>
        </section>

        {/* ═══ 3. TOP FILTER & PRODUCT GRID (KHÔNG DÙNG SIDEBAR) ═══ */}
        <Container>
          <div className={styles.singleSectorLayout}>
            {/* TopFilterBar với các nhóm con của ngành hàng (ví dụ: các nhóm Cà phê) */}
            <TopFilterBar
              totalCount={productCount || 0}
              visibleCount={products?.length || 0}
              selectedDmnvts={selectedDmnvts}
              onDmnvtChange={setSelectedDmnvts}
              allDmnvts={allDmnvts || []}
              allowedNganhHang={allowedNganhHang}
              groupDefinitions={groupDefinitions}
              sortQuery={sortQuery}
              onSortChange={setSortQuery}
              gridCols={gridCols}
              onGridColsChange={setGridCols}
              onResetAll={handleReset}
              isSingleSector={true}
              locale={locale}
            />

            {/* Products Grid */}
            <div
              className={`${styles.grid} ${
                gridCols === 3 ? styles.gridCols3 : styles.gridCols4
              }`}
            >
              {isLoading && !isFetched ? (
                skeletons.map((_, i) => (
                  <div key={`skel-${i}`} className={styles.gridItem}>
                    <ProductCard isLoading locale={locale} />
                  </div>
                ))
              ) : products && products.length > 0 ? (
                products.map((product: any) => (
                  <div key={product._id} className={styles.gridItem}>
                    <ProductCard product={product} locale={locale} />
                  </div>
                ))
              ) : (
                <div className={styles.empty}>
                  <p>
                    {locale === "en"
                      ? "No matching products found."
                      : "Không tìm thấy sản phẩm phù hợp."}
                  </p>
                  {selectedDmnvts.length > 0 && (
                    <button
                      onClick={handleReset}
                      className="mt-2 text-xs text-brand-600 underline font-medium cursor-pointer"
                    >
                      {locale === "en" ? "Clear filters" : "Xóa bộ lọc"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Pagination */}
            <div className={styles.paginationWrap}>
              <Pagination
                page={page}
                total={totalPages}
                onChange={setPage}
                totalCount={productCount || 0}
                visibleCount={products?.length || 0}
                locale={locale}
              />
            </div>
          </div>
        </Container>

        {/* ═══ 4. PRODUCT INTRODUCTION / SHOWCASE SECTION (SO LE) ═══ */}
        {isIntroSectionVisible && introItems.length > 0 && (
          <section
            className={styles.introShowcaseSection}
            style={
              { "--sector-color": sectorThemeColor } as React.CSSProperties
            }
          >
            <Container>
              {introSectionConfig?.summary && (
                <div className={styles.introSectionHeader}>
                  <Title
                    as="h2"
                    size="large"
                    align="center"
                    color="#0b5d3b"
                    className={styles.introSectionMainTitle}
                  >
                    {introSectionConfig.summary}
                  </Title>
                  {introSectionConfig?.content_2 && (
                    <Description as="p" size="medium" align="center">
                      {introSectionConfig.content_2}
                    </Description>
                  )}
                </div>
              )}

              <div className={styles.introItemsList}>
                {introItems.map((item: any, idx: number) => {
                  const isImageRight = idx % 2 === 1;
                  return (
                    <motion.div
                      key={item?._id || item?.__id_tmp__ || `intro-${idx}`}
                      className={`${styles.introItem} ${
                        isImageRight ? styles.introItemReverse : ""
                      }`}
                      initial={{ opacity: 0, y: 36 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                      {/* Cột hình ảnh */}
                      <div className={styles.introImageCol}>
                        {item?.picture && (
                          <div className={styles.introImageWrap}>
                            <Image
                              src={appendImageUrlFromAPI({
                                src: item.picture,
                                size: "xl",
                              })}
                              alt={item.summary || "Giới thiệu sản phẩm"}
                              width={560}
                              height={420}
                              className={styles.introImg}
                            />
                          </div>
                        )}
                      </div>

                      {/* Cột văn bản (Title & Description) */}
                      <div className={styles.introTextCol}>
                        {item?.summary && (
                          <Title
                            as="h3"
                            size="medium"
                            align="left"
                            color="#0d5735"
                            className={styles.introItemTitle}
                          >
                            {item.summary}
                          </Title>
                        )}
                        {item?.content && (
                          <Description
                            as="div"
                            size="medium"
                            align="justify"
                            className={styles.introItemContent}
                          >
                            <div
                              dangerouslySetInnerHTML={{ __html: item.content }}
                            />
                          </Description>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </Container>
          </section>
        )}
      </div>
    </ProductProvider>
  );
}
